import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';

import { MatCard, MatCardContent } from '@angular/material/card';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

interface Assignee {
  id: string;
  name: string;
}

@Component({
  selector: 'shared-assignee-picker',
  standalone: true,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    NgClass,
    MatCard,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatPrefix,
    MatInput,
    MatIcon,
    MatTooltip,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './assignee-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssigneePickerComponent {
  currentAssignee = input<Assignee | null>(null);
  assignees = input<Assignee[]>([]);
  assigneeChanged = output<Assignee | null>();

  protected readonly isOpen = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly searchTerm = signal('');

  protected readonly filteredAssignees = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term) return this.assignees();

    return this.assignees().filter(a => a.name.toLowerCase().includes(term));
  });

  protected readonly computedCurrentAssignee = computed(() => this.currentAssignee());

  protected readonly currentAssigneeInitials = computed(() => {
    const name = this.computedCurrentAssignee()?.name;

    return name ? this.getInitials(name) : '';
  });

  constructor() {
    this.searchControl.valueChanges.subscribe(value => this.searchTerm.set(value));
  }

  protected toggleOverlay(): void {
    if (this.isOpen()) this.isOpen.set(false);
    else this.isOpen.set(true);
  }

  protected selectAssignee(assignee: Assignee): void {
    this.assigneeChanged.emit({
      id: assignee.id,
      name: assignee.name,
    });
    this.closeOverlay();
  }

  protected closeOverlay(): void {
    this.isOpen.set(false);
    this.searchControl.reset();
    this.searchTerm.set('');
  }

  protected getInitials(fullName: string): string {
    const nameParts = fullName.trim().split(/\s+/);

    if (nameParts.length >= 2) return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    else if (nameParts.length === 1) return nameParts[0][0].toUpperCase();
    else return '';
  }
}
